// ==================== AUTHENTICATION CONTEXT - FIXED ====================

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/auth';
import { STORAGE_KEYS, USER_ROLES, PERMISSIONS } from '../utils/constants';

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
const AuthActionTypes = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  UPDATE_USER: 'UPDATE_USER',
};

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AuthActionTypes.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case AuthActionTypes.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case AuthActionTypes.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case AuthActionTypes.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
      };
    case AuthActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };
    case AuthActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    case AuthActionTypes.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing token on app load
  useEffect(() => {
    const checkAuthState = async () => {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);

      if (token && userData) {
        try {
          // Parse stored user data
          const user = JSON.parse(userData);

          // Verify token is still valid
          await authService.getCurrentUser(token);

          dispatch({
            type: AuthActionTypes.LOGIN_SUCCESS,
            payload: { user, token },
          });
        } catch (error) {
          // Token is invalid, clear storage
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER_DATA);
          dispatch({ type: AuthActionTypes.LOGOUT });
        }
      } else {
        dispatch({ type: AuthActionTypes.SET_LOADING, payload: false });
      }
    };

    checkAuthState();
  }, []);

  // Login function - FIXED
  const login = async (email, password) => {
    dispatch({ type: AuthActionTypes.LOGIN_START });

    try {
      const response = await authService.login(email, password);

      // FIXED: Handle the actual API response structure
      let token = null;
      let user = null;

      // Check multiple possible response structures
      if (response.Token) {
        // Case 1: Token is in the response (capitalized)
        token = response.Token;
        user = response.User;
      } else if (response.token) {
        // Case 2: token is lowercase
        token = response.token;
        // eslint-disable-next-line no-unused-vars
        const { token: _, ...userData } = response;
        user = userData;
      } else if (response.data) {
        // Case 3: Token might be nested in data
        token = response.data.Token || response.data.token;
        user = response.data.User || response.data.user;
      } else {
        // Case 4: Check response headers for token
        const authHeader =
          response.headers?.['authorization'] ||
          response.headers?.['Authorization'];
        if (authHeader) {
          token = authHeader.replace('Bearer ', '');
          user = response.User || response;
        }
      }

      // Handle case where API returns Token: null
      if (!token || token === null) {
        throw new Error('No authentication token received from server');
      }

      // Store in localStorage
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));

      dispatch({
        type: AuthActionTypes.LOGIN_SUCCESS,
        payload: { user, token },
      });

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage =
        typeof error === 'string' ? error : error.message || 'Login failed';
      dispatch({
        type: AuthActionTypes.LOGIN_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    dispatch({ type: AuthActionTypes.LOGOUT });
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AuthActionTypes.CLEAR_ERROR });
  };

  // Role-based permission checks
  const hasPermission = permission => {
    if (!state.user) return false;

    const userRole =
      state.user.role?.toLowerCase() ||
      (state.user.Roles && state.user.Roles[0]?.toLowerCase());

    const permissions = {
      // Admin permissions
      [USER_ROLES.ADMIN]: [
        PERMISSIONS.VIEW_USERS,
        PERMISSIONS.MANAGE_USERS,
        PERMISSIONS.CREATE_USERS,
        PERMISSIONS.ACTIVATE_USER,
        PERMISSIONS.DEACTIVATE_USER,
        PERMISSIONS.VIEW_CERTIFICATES,
        PERMISSIONS.DELETE_CERTIFICATES,
        PERMISSIONS.VIEW_STATISTICS,
      ],
      // Office permissions
      [USER_ROLES.OFFICE]: [
        PERMISSIONS.CREATE_CERTIFICATES,
        PERMISSIONS.VIEW_CERTIFICATES,
        PERMISSIONS.EDIT_CERTIFICATES,
        PERMISSIONS.DELETE_CERTIFICATES,
        PERMISSIONS.VIEW_STATISTICS,
      ],
    };

    return permissions[userRole]?.includes(permission) || false;
  };

  // Check if user is admin
  const isAdmin = () => {
    return (
      state.user?.role?.toLowerCase() === USER_ROLES.ADMIN ||
      (state.user?.Roles &&
        state.user.Roles.some(role => role.toLowerCase() === 'admin'))
    );
  };

  // Check if user is office
  const isOffice = () => {
    return (
      state.user?.role?.toLowerCase() === USER_ROLES.OFFICE ||
      (state.user?.Roles &&
        state.user.Roles.some(role => role.toLowerCase() === 'office'))
    );
  };

  const value = {
    ...state,
    login,
    logout,
    clearError,
    hasPermission,
    isAdmin,
    isOffice,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
